'use client';

import React, { Children } from 'react';
import styled from 'styled-components';
import { theme } from '@/config/theme';

export interface InfoCardsLayoutProps {
  children: React.ReactNode;
  minWidth?: string;
  style?: React.CSSProperties;
  dividerHeight?: number;
  isVertical?: boolean;
}

const Grid = styled.div<{ $isVertical?: boolean }>`
  margin: 24px;
  padding: 14.5px 24px;
  display: flex;
  flex-wrap: wrap;
  flex-direction: ${props => props.$isVertical ? 'column' : 'row'};
  align-items: stretch;
  justify-content: ${props => props.$isVertical ? 'space-around' : 'space-between'};
  gap: 16px;
  border: 1px solid ${theme.colors.border.primary};
  border-radius: 8px;
  max-width: ${props => props.$isVertical ? '400px' : '100%'};

  @media (max-width: 1024px) {
    flex-wrap: ${props => props.$isVertical ? 'nowrap' : 'wrap'};
  }

  @media (max-width: 640px) {
    flex-direction: column;
  }
`;

const CardWrapper = styled.div<{ $minWidth: string, $isVertical?: boolean }>`
  min-width: ${props => props.$minWidth};
  padding: 5px 0;
  display: flex;
  align-items: center;
  justify-content: ${props => props.$isVertical ? 'flex-start' : 'center'};
`;

const Divider = styled.div<{ $height?: number; $isVertical?: boolean }>`
  width: ${props => props.$isVertical ? '100%' : '1px'};
  height: ${props => props.$isVertical ? '1px' : (props.$height ? props.$height + 'px' : '100%')};
  background: ${theme.colors.border.primary};
  align-self: center;
  
  @media (max-width: 640px) {
    width: 100%;
    height: 1px;
    margin: 16px 0;
  }
`;

/**
 * InfoCardsLayout Component
 * 
 * A layout component that arranges InfoCard components in a horizontal or vertical grid
 * with automatic dividers between cards. Responsive design adapts to mobile.
 * 
 * @component
 * @example
 * ```tsx
 * <InfoCardsLayout>
 *   <InfoCard label="Status" value="Active" />
 *   <InfoCard label="Count" value="100" />
 *   <InfoCard label="Score" value="95%" />
 * </InfoCardsLayout>
 * 
 * <InfoCardsLayout isVertical>
 *   <InfoCard label="Status" value="Active" />
 *   <InfoCard label="Count" value="100" />
 * </InfoCardsLayout>
 * ```
 */
export const InfoCardsLayout: React.FC<InfoCardsLayoutProps> = ({ 
  children,
  minWidth = '280px', 
  style, 
  dividerHeight = 75,
  isVertical = false 
}) => {
  // Convert children to array and filter out null/undefined
  const childArray = Children.toArray(children).filter(Boolean);

  return (
    <Grid style={style} $isVertical={isVertical}>
      {childArray.map((child, index) => (
        <React.Fragment key={index}>
          <CardWrapper $minWidth={minWidth} $isVertical={isVertical}>{child}</CardWrapper>
          {/* Add divider after each card except the last one */}
          {index < childArray?.length - 1 && <Divider $height={dividerHeight} $isVertical={isVertical} />}
        </React.Fragment>
      ))}
    </Grid>
  );
};

